/**
 * Trovva — Agent Performance Scoring
 *
 * Pure, deterministic functions for computing per-agent metrics,
 * composite scores, letter grades, segments, and anomaly events
 * from the task log.
 *
 * Design notes:
 *   - 100% data-in, data-out — no React, no fetching, trivial to unit test
 *   - Graceful on sparse data: an agent with 0 tasks returns a "Ramping" segment
 *     and score of null rather than a misleading 0
 *   - Thresholds live at the top as named constants so broker can eventually
 *     tune them per-org without re-reading every function
 */

// ── Weights ─────────────────────────────────────────────────────
export const WEIGHTS = {
  completion: 0.40,
  onTime: 0.25,
  recency: 0.15,
  conversion: 0.20,
};

// ── Thresholds ──────────────────────────────────────────────────
export const THRESHOLDS = {
  gradeA: 90, gradeB: 75, gradeC: 60, gradeD: 45,
  topSegment: 85,
  needsAttentionScore: 60,
  needsAttentionOverdue: 5,
  needsAttentionInactiveDays: 3,
  rampingWindowDays: 60,
  anomalyScoreDropPts: 15,
  anomalyConversionDropPct: 20,
};

// ── Date helpers ────────────────────────────────────────────────
const ONE_DAY_MS = 86_400_000;
function daysBetween(aIso, bIso) {
  try { return Math.floor((new Date(bIso).getTime() - new Date(aIso).getTime()) / ONE_DAY_MS); }
  catch { return 0; }
}
function todayISO() { return new Date().toISOString().split('T')[0]; }

// ── Per-agent metrics ───────────────────────────────────────────
/**
 * Compute raw metrics for one agent from their task list.
 * Input: array of task objects with at least
 *   { status: 'pending'|'completed', dueDate: 'YYYY-MM-DD', completedDate?: 'YYYY-MM-DD', createdDate?: 'YYYY-MM-DD' }
 */
export function computeAgentMetrics(tasks, now = todayISO()) {
  const total = tasks.length;
  const completed = tasks.filter(t => t.status === 'completed').length;
  const pending = total - completed;
  const overdue = tasks.filter(t => t.status !== 'completed' && t.dueDate < now).length;
  const onTime = tasks.filter(t =>
    t.status === 'completed' && t.completedDate && t.completedDate <= t.dueDate
  ).length;

  // Activity recency: most recent completed OR created date, in days
  let lastActivity = null;
  tasks.forEach(t => {
    const d = t.completedDate || t.createdDate;
    if (d && (!lastActivity || d > lastActivity)) lastActivity = d;
  });
  const daysSinceActivity = lastActivity ? Math.max(0, daysBetween(lastActivity, now)) : 999;

  const completionRate = total > 0 ? completed / total : null;
  const onTimeRate = completed > 0 ? onTime / completed : null;

  return {
    total, completed, pending, overdue, onTime,
    completionRate, onTimeRate,
    daysSinceActivity, lastActivity,
  };
}

/**
 * Lead conversion rate for an agent given their assigned leads.
 * Input: array of leads with at least { lead_status } (see contacts schema).
 * "Converted" = moved past early-funnel stages.
 */
export function computeLeadConversion(leads) {
  if (!leads || !leads.length) return { total: 0, converted: 0, rate: null };
  const earlyStages = new Set(['new', 'contacted']);
  const total = leads.length;
  const converted = leads.filter(l =>
    l.lead_status && !earlyStages.has(l.lead_status) && l.lead_status !== 'lost'
  ).length;
  return { total, converted, rate: total ? converted / total : null };
}

// ── Composite score ─────────────────────────────────────────────
/**
 * Build the weighted composite 0–100 for one agent.
 * Any dimension with null data is dropped and weights renormalize.
 * Returns null if nothing is scorable (true ramping case).
 */
export function compositeScore(metrics, conversion) {
  const dims = [];
  if (metrics.completionRate != null) dims.push({ w: WEIGHTS.completion, v: metrics.completionRate * 100 });
  if (metrics.onTimeRate != null)     dims.push({ w: WEIGHTS.onTime,     v: metrics.onTimeRate * 100 });

  // Recency converts to 0–100: 0 days = 100, 7+ days = 0
  const recencyV = Math.max(0, 100 - (metrics.daysSinceActivity * (100 / 7)));
  dims.push({ w: WEIGHTS.recency, v: recencyV });

  if (conversion?.rate != null) dims.push({ w: WEIGHTS.conversion, v: conversion.rate * 100 });

  if (!dims.length) return null;
  const totalW = dims.reduce((a, d) => a + d.w, 0);
  const score = dims.reduce((a, d) => a + d.v * d.w, 0) / totalW;
  return Math.round(score);
}

export function letterGrade(score) {
  if (score == null) return '—';
  if (score >= THRESHOLDS.gradeA) return 'A';
  if (score >= THRESHOLDS.gradeB) return 'B';
  if (score >= THRESHOLDS.gradeC) return 'C';
  if (score >= THRESHOLDS.gradeD) return 'D';
  return 'F';
}

export function gradeColor(grade, themeColors) {
  const map = {
    A: themeColors.success,
    B: '#4CAF50',
    C: themeColors.warning,
    D: '#E08B3B',
    F: themeColors.danger,
    '—': themeColors.textLight,
  };
  return map[grade] || themeColors.textLight;
}

// ── Segments ────────────────────────────────────────────────────
/**
 * Classify an agent into ONE segment.
 * Precedence: ramping → needs_attention → top → on_track
 */
export function segmentFor(agent, metrics, score, now = new Date()) {
  // Ramping: hired within window (falls back if no hire date on mock data)
  if (agent.hired_at) {
    const days = Math.max(0, Math.floor((now.getTime() - new Date(agent.hired_at).getTime()) / ONE_DAY_MS));
    if (days <= THRESHOLDS.rampingWindowDays) return 'ramping';
  }
  if (metrics.total === 0) return 'ramping'; // no tasks yet → treat as ramping

  // Needs attention: composite below threshold OR lots of overdue OR stale
  if (score != null && score < THRESHOLDS.needsAttentionScore) return 'needs_attention';
  if (metrics.overdue >= THRESHOLDS.needsAttentionOverdue) return 'needs_attention';
  if (metrics.daysSinceActivity >= THRESHOLDS.needsAttentionInactiveDays) return 'needs_attention';

  if (score != null && score >= THRESHOLDS.topSegment) return 'top';
  return 'on_track';
}

export const SEGMENT_META = {
  needs_attention: { key: 'needs_attention', label: 'Needs attention', emoji: '🔴', color: '#EF4444' },
  on_track:        { key: 'on_track',        label: 'On track',        emoji: '🟡', color: '#F59E0B' },
  top:             { key: 'top',             label: 'Top performers',  emoji: '🟢', color: '#10B981' },
  ramping:         { key: 'ramping',         label: 'Ramping',         emoji: '🆕', color: '#3B82F6' },
};

// ── Trend sparkline ─────────────────────────────────────────────
/**
 * Produce a 14-point series of daily completion counts (last 14 days).
 * Returns { series: [n0,n1,...,n13], trend: 'up'|'down'|'flat', delta }.
 */
export function computeTrend(tasks, now = new Date()) {
  const days = 14;
  const series = new Array(days).fill(0);
  for (let i = 0; i < days; i++) {
    const d = new Date(now.getTime() - (days - 1 - i) * ONE_DAY_MS);
    const key = d.toISOString().split('T')[0];
    series[i] = tasks.filter(t => t.completedDate === key).length;
  }
  // Compare last 7 vs prior 7
  const firstHalf = series.slice(0, 7).reduce((a, v) => a + v, 0);
  const secondHalf = series.slice(7).reduce((a, v) => a + v, 0);
  const delta = secondHalf - firstHalf;
  const trend = delta > 1 ? 'up' : delta < -1 ? 'down' : 'flat';
  return { series, trend, delta };
}

// ── Anomaly / Triage events ─────────────────────────────────────
/**
 * Given the roster (agent → { metrics, score, conversion, trend, prevScore }),
 * return an ordered list of triage events. Severity roughly: 3 = red, 2 = amber, 1 = info.
 */
export function computeTriageEvents(roster) {
  const events = [];

  // Tone guide for quickPings: warm, supportive, never accusatory.
  // The broker can always edit before sending — the default should
  // open a conversation, not put the agent on the defensive.
  for (const a of roster) {
    const first = (a.name || '').split(' ')[0];

    // Inactivity
    if (a.metrics.daysSinceActivity >= 5) {
      events.push({
        severity: 3, agentName: a.name, kind: 'inactive',
        label: `${first} hasn't logged activity in ${a.metrics.daysSinceActivity} days`,
        quickPing: `Hi ${first}, haven't caught up with you in a bit — everything going okay? Let me know if there's anything I can help with.`,
      });
    } else if (a.metrics.daysSinceActivity >= THRESHOLDS.needsAttentionInactiveDays) {
      events.push({
        severity: 2, agentName: a.name, kind: 'inactive',
        label: `${first} inactive ${a.metrics.daysSinceActivity} days`,
        quickPing: `Hi ${first}, just checking in — anything I can help unblock?`,
      });
    }

    // Overdue pile
    if (a.metrics.overdue >= 10) {
      events.push({
        severity: 3, agentName: a.name, kind: 'overdue',
        label: `${first} has ${a.metrics.overdue} overdue tasks`,
        quickPing: `Hi ${first}, looks like a few tasks have piled up. Happy to jump on a quick call if you'd like help prioritizing.`,
      });
    } else if (a.metrics.overdue >= THRESHOLDS.needsAttentionOverdue) {
      events.push({
        severity: 2, agentName: a.name, kind: 'overdue',
        label: `${first} — ${a.metrics.overdue} overdue`,
        quickPing: `Hi ${first}, you have a couple of tasks that need your attention. Let me know if I can help.`,
      });
    }

    // Score drop week-over-week
    if (a.prevScore != null && a.score != null && (a.prevScore - a.score) >= THRESHOLDS.anomalyScoreDropPts) {
      events.push({
        severity: 3, agentName: a.name, kind: 'score_drop',
        label: `${first}: score dropped ${a.prevScore - a.score} pts this week (${a.prevScore} → ${a.score})`,
        quickPing: `Hi ${first}, looks like this week has been a bit slower. Would a quick 15-min chat tomorrow help?`,
      });
    }

    // Low-grade anchor
    if (a.score != null && a.score < 45) {
      events.push({
        severity: 3, agentName: a.name, kind: 'low_grade',
        label: `${first} performance score is ${a.score} (F)`,
        quickPing: `Hi ${first}, want to grab 30 min this week to walk through your pipeline together? I'd love to help where I can.`,
      });
    }

    // Trend down sharply
    if (a.trend?.trend === 'down' && a.trend?.delta <= -3) {
      events.push({
        severity: 2, agentName: a.name, kind: 'trend_down',
        label: `${first}'s completion down ${Math.abs(a.trend.delta)} vs last week`,
        quickPing: `Hi ${first}, checking in — anything blocking you this week? Let me know if I can help.`,
      });
    }

    // Large backlog of pending work (not just overdue — currently-open load)
    if (a.metrics.pending >= 20) {
      events.push({
        severity: 2, agentName: a.name, kind: 'backlog',
        label: `${first} is carrying ${a.metrics.pending} pending tasks`,
        quickPing: `Hi ${first}, you've got a lot on your plate. Want help prioritizing, or should I redistribute a few?`,
      });
    }

    // Quality-of-work signal: low on-time completion rate
    if (a.metrics.completed >= 5 && a.metrics.onTimeRate != null && a.metrics.onTimeRate < 0.5) {
      events.push({
        severity: 2, agentName: a.name, kind: 'late_completion',
        label: `${first} completing only ${Math.round(a.metrics.onTimeRate * 100)}% of tasks on time`,
        quickPing: `Hi ${first}, let's take a look at your task load together — I'd like to help make the timelines more realistic.`,
      });
    }

    // Pipeline gap — agent has barely any active work (may need more leads)
    if (a.metrics.total > 0 && a.metrics.pending <= 1 && a.metrics.total < 5) {
      events.push({
        severity: 1, agentName: a.name, kind: 'empty_pipeline',
        label: `${first} has a light pipeline — ${a.metrics.pending} open task${a.metrics.pending !== 1 ? 's' : ''}`,
        quickPing: `Hi ${first}, looks like you have room for more. Want me to route a few fresh leads your way?`,
      });
    }
  }

  // Tag each event with its "kind" for the default inactivity / overdue rules
  // so the dedup logic can skip exact duplicates. Inactivity and overdue rules
  // above set kinds inline below — fill in here in case older rules above
  // missed it. (Belt-and-suspenders; keeps behavior forwards-compatible.)
  for (const e of events) if (!e.kind) e.kind = 'generic';

  // Dedup: only collapse EXACT duplicates (same agent + same kind). A single
  // agent can have multiple distinct events — e.g. "5 overdue" AND "inactive
  // 4 days" — which reflects the reality that an agent may be behind on
  // several different dimensions at once.
  const seen = new Set();
  const deduped = [];
  for (const e of events) {
    const key = `${e.agentName}::${e.kind}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(e);
  }

  // Cap per agent at 2 events so one person doesn't drown the feed.
  const perAgent = new Map();
  const capped = [];
  // Sort first so the highest-severity events for each agent win the cap.
  deduped.sort((a, b) => b.severity - a.severity || a.agentName.localeCompare(b.agentName));
  for (const e of deduped) {
    const n = perAgent.get(e.agentName) || 0;
    if (n >= 2) continue;
    capped.push(e);
    perAgent.set(e.agentName, n + 1);
  }

  return capped;
}

// ── Fuzzy search ────────────────────────────────────────────────
/**
 * Substring + Levenshtein-1 matcher. Returns a scored ranking of
 * agents (0 = no match, higher = better).
 * Use: fuzzySearchAgents(query, agents).filter(r => r.score > 0)
 */
export function fuzzySearchAgents(query, agents) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return agents.map(a => ({ agent: a, score: 1 }));
  const results = [];
  for (const a of agents) {
    const n = (a.name || '').toLowerCase();
    if (!n) continue;
    let score = 0;
    // Exact substring = highest
    if (n.startsWith(q)) score = 1000;
    else if (n.includes(q)) score = 500;
    else {
      // Per-token substring (first-name match)
      const tokens = n.split(/\s+/);
      for (const t of tokens) {
        if (t.startsWith(q)) { score = 400; break; }
        if (t.includes(q)) { score = 200; break; }
      }
      // Levenshtein ≤ 2 over any token
      if (!score) {
        for (const t of tokens) {
          const d = levenshtein(t, q);
          if (d <= 1) { score = 100; break; }
          if (d === 2 && q.length > 3) { score = 50; break; }
        }
      }
    }
    if (score) results.push({ agent: a, score });
  }
  results.sort((a, b) => b.score - a.score || a.agent.name.localeCompare(b.agent.name));
  return results;
}

function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    let curr = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr.push(Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost));
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}

// ── Full roster compile ─────────────────────────────────────────
/**
 * One-shot: compile the full roster view from raw inputs.
 * agents:  [{ name, id?, hired_at?, role? }]
 * tasksByAgent: { [agentName]: [...tasks] }
 * leadsByAgent: { [agentName]: [...leads] }  (optional)
 * prevScores:   { [agentName]: prevWeekScore } (optional)
 */
export function buildRoster(agents, tasksByAgent, leadsByAgent = {}, prevScores = {}) {
  return agents.map(a => {
    const tasks = tasksByAgent[a.name] || [];
    const leads = leadsByAgent[a.name] || [];
    const metrics = computeAgentMetrics(tasks);
    const conversion = computeLeadConversion(leads);
    const score = compositeScore(metrics, conversion);
    const grade = letterGrade(score);
    const segment = segmentFor(a, metrics, score);
    const trend = computeTrend(tasks);
    return {
      ...a,
      metrics,
      conversion,
      score,
      grade,
      segment,
      trend,
      prevScore: prevScores[a.name] ?? null,
      scoreBreakdown: {
        completion: metrics.completionRate != null ? Math.round(metrics.completionRate * 100) : null,
        onTime: metrics.onTimeRate != null ? Math.round(metrics.onTimeRate * 100) : null,
        recency: Math.round(Math.max(0, 100 - (metrics.daysSinceActivity * (100 / 7)))),
        conversion: conversion.rate != null ? Math.round(conversion.rate * 100) : null,
      },
    };
  });
}
